/**
 * SortSelect — the "Sort  Alphabetical ⌄" pill from the design.
 *
 * A native `<select>` under the styling, not a custom listbox. Sort order is
 * exactly what a select is for, and the native control brings keyboard
 * handling, type-ahead and the platform's own picker on a phone — all of which
 * a hand-rolled menu would have to reimplement to reach parity.
 *
 * Overview's roster uses a segmented control for the same job because it has
 * three or four options and room to show them; this is for the case where the
 * options do not fit on the row.
 */
import { ChevronDown } from 'lucide-react';

export interface SortSelectOption {
  id: string;
  label: string;
}

export interface SortSelectProps {
  value: string | undefined;
  onChange: (id: string) => void;
  options: readonly SortSelectOption[];
  ariaLabel: string;
}

export function SortSelect({ value, onChange, options, ariaLabel }: SortSelectProps) {
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 34,
        padding: '0 10px 0 14px',
        borderRadius: 999,
        border: '1px solid var(--nb-border)',
        background: 'var(--nb-bg)',
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--nb-subtle)' }}>Sort</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nb-ink)' }}>
        {options.find((option) => option.id === value)?.label ?? ''}
      </span>
      <ChevronDown size={14} aria-hidden="true" style={{ color: 'var(--nb-subtle)' }} />
      {/* The real control, laid over the pill: it keeps every native behaviour
          while the styled text above is what gets drawn. */}
      <select
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        aria-label={ariaLabel}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          cursor: 'pointer',
          appearance: 'none',
        }}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
